"use client";

import React, { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Loader2,
  Video,
  Eye,
  Pencil,
  Trash2,
  X,
  Music,
  Info,
  Settings2,
} from "lucide-react";
import {
  Camera,
  Shot,
  ShotPart,
  ShotCharacter,
} from "@/app/types/shot-breakdown.types";
import {
  SongwriterBlock,
  songwriterStyles,
} from "@/app/songwriter/types/songwriter.types";
import { FormikInput, FormikSelect } from "@/app/components/input/inputs";
import ModalLayout from "@/app/components/modal/modal-layout";
import AddLyricalShotForm from "../components/add-lyrical-shot-modal";
import ViewLyricalShotModal from "../components/view-lyrical-shot-modal";
import { Header } from "@/app/components/header";

interface LyricalBreakdownViewProps {
  blocks: SongwriterBlock[];
  isLoading: boolean;
  shots: Shot[];
  cameras: Camera[];
  movieCharacters: ShotCharacter[];
  sceneType: string;
  autosaveStatus: "idle" | "typing" | "saving" | "saved" | "error";
  selectionMenu: {
    top: number;
    left: number;
    parts: ShotPart[];
  } | null;
  clearSelection: () => void;
  handleMouseUp: () => void;
  initializeProduction: (count: number, type: string) => Promise<any> | any;
  addShot: (shotDetails: Omit<Shot, "id" | "parts">) => void;
  updateShot: (
    shotId: string | number,
    updatedDetails: Omit<Shot, "id" | "parts">,
  ) => void;
  deleteShot: (shotId: string | number) => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
  headerChildren?: React.ReactNode;
}

// Lightweight Production Setup Modal (Streamlined specifically for songs - asks ONLY for camera count)
const LyricalProductionSetupModal = ({
  initializeProduction,
}: {
  initializeProduction: (count: number, type: string) => Promise<any> | any;
}) => {
  return (
    <div className="min-h-[40vh] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-blumine-800 tracking-tight">
            Song Production Setup
          </h2>
          <p className="text-xs text-slate-500 mt-2 font-semibold">
            Configure your camera setup to begin placing camera breakdown shots.
          </p>
        </div>

        <div className="p-5 md:p-7 bg-slate-50/50 rounded-2xl border border-slate-100">
          <Formik
            initialValues={{
              numCameras: 1,
            }}
            validationSchema={Yup.object({
              numCameras: Yup.number()
                .min(1, "Must be at least 1")
                .max(20, "Cannot add more than 20 cameras")
                .required("Number of cameras is required"),
            })}
            onSubmit={async (values) => {
              await initializeProduction(values.numCameras, "SONG");
            }}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-5 text-left">
                <FormikInput
                  label="Number of Cameras for this Song"
                  name="numCameras"
                  type="number"
                  min={1}
                  max={20}
                  enableRedAsterick
                />

                <div className="flex gap-3 bg-white border border-slate-100 p-4 rounded-xl">
                  <Info className="w-4 h-4 text-blumine-500 shrink-0 mt-0.5" />
                  <p className="text-[12px] text-slate-600 leading-relaxed font-medium">
                    <span className="font-bold text-blumine-800">Note:</span>{" "}
                    Setup must be configured before placing camera targets on
                    lyrics lines.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-11 rounded-full bg-blumine-600 text-white text-sm md:text-base font-bold shadow-md transition-all duration-300 hover:bg-blumine-700 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isSubmitting ? "Initializing Setup..." : "Complete Setup"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export function LyricalBreakdownView({
  blocks,
  isLoading,
  shots,
  cameras,
  movieCharacters,
  autosaveStatus,
  selectionMenu,
  clearSelection,
  handleMouseUp,
  initializeProduction,
  addShot,
  updateShot,
  deleteShot,
  menuRef,
  headerChildren,
}: LyricalBreakdownViewProps) {
  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";
  const containerRef = useRef<HTMLDivElement>(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [toggledShotId, setToggledShotId] = useState<number | string | null>(
    null,
  );
  const [viewingShotId, setViewingShotId] = useState<number | string | null>(
    null,
  );
  const [updatingShotId, setUpdatingShotId] = useState<number | string | null>(
    null,
  );
  const [isInitModalOpen, setIsInitModalOpen] = useState(false);

  const handleCloseModal = () => {
    clearSelection();
  };

  // Client-side font setup
  useEffect(() => {
    const fontId = "google-font-courier-prime-songwriter";
    const styleId = "songwriter-editor-force-courier";

    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        [data-songwriter-editor] *, 
        [data-songwriter-editor] div, 
        [data-songwriter-editor] span {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, [COURIER_STACK]);

  const activeViewingShot = shots.find((s) => s.id === viewingShotId);

  // Slices character indexes and overlays marker-pen highlighter gradient (matches screenplay shot-breakdown blumine/teal aesthetics)
  const renderBlockText = (block: SongwriterBlock) => {
    if (!toggledShotId) return block.text;

    const toggledShot = shots.find((s) => s.id === toggledShotId);
    if (!toggledShot) return block.text;

    const blockShotParts = toggledShot.parts
      .filter((p) => p.block_id === block.id)
      .sort((a, b) => a.start_index - b.start_index);

    if (blockShotParts.length === 0) return block.text;

    const nodes: React.ReactNode[] = [];
    let currentIndex = 0;

    blockShotParts.forEach((part, index) => {
      const actualStart = Math.max(part.start_index, currentIndex);

      if (actualStart > currentIndex) {
        nodes.push(
          <span key={`text-${currentIndex}`}>
            {block.text.slice(currentIndex, actualStart)}
          </span>,
        );
      }

      if (actualStart < part.end_index) {
        nodes.push(
          <span
            key={`shot-${toggledShot.id}-${index}`}
            style={{
              backgroundImage:
                "linear-gradient(104deg, transparent 0.5%, rgba(176, 227, 241, 0.7) 2%, rgba(122, 206, 230, 0.8) 5%, rgba(176, 227, 241, 0.7) 95%, transparent 99%)",
              borderRadius: "6px 2px 5px 3px / 2px 6px 3px 5px",
              boxDecorationBreak: "clone",
              WebkitBoxDecorationBreak: "clone",
              padding: "0.15em 0.25em",
              margin: "0 -0.15em",
            }}
          >
            {block.text.slice(actualStart, part.end_index)}
          </span>,
        );
      }
      currentIndex = Math.max(currentIndex, part.end_index);
    });

    if (currentIndex < block.text.length) {
      nodes.push(
        <span key={`text-${currentIndex}`}>
          {block.text.slice(currentIndex)}
        </span>,
      );
    }

    return nodes;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4 w-full">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        <p className="text-sm font-medium text-zinc-500 animate-pulse">
          Loading Lyrical Breakdown Studio...
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center justify-center w-full"
      ref={containerRef}
    >
      <div className="relative w-full min-h-screen flex flex-col xl:flex-row bg-zinc-50/20 transition-all duration-300">
        <div className="flex-1 flex flex-col min-w-0">
          <Header>
            {headerChildren}
          </Header>
          <div 
            className="relative p-8 md:p-12 mx-auto w-full flex flex-col gap-8"
            onClick={() => setToggledShotId(null)}
          >
      {/* Interactive Manuscript Lyric Sheet Area */}
      <div className="flex items-center justify-center max-w-6xl xl:max-w-full">
        <div className="flex flex-col gap-4 min-h-screen">
          <div
            className="relative bg-[#fdfdfc] shadow-md shadow-zinc-300/60 border border-zinc-100 rounded-sm md:rounded-md pl-[1.5in] py-[1in] pr-[1in] flex flex-col w-[210mm] min-h-auto shrink-0"
            style={{
              fontFamily: COURIER_STACK,
              paddingLeft: "1.5in",
              paddingRight: "1in",
              paddingTop: "1in",
              paddingBottom: "1in",
              lineHeight: "1.2",
            }}
            data-songwriter-editor="true"
            onMouseUp={handleMouseUp}
          >
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Music className="w-8 h-8 text-slate-300 mb-3" />
                <p className="text-xs text-slate-400 font-mono italic">
                  [ No active lyrics written yet. Return to "Write Lyrics" page
                  to draft your music! ]
                </p>
              </div>
            ) : (
              blocks.map((block) => (
                <div
                  key={block.id}
                  data-block-id={block.id}
                  className={`relative break-words w-full px-4 py-2 hover:bg-slate-50/40 rounded-sm transition duration-150 border border-transparent ${songwriterStyles[block.type].className}`}
                  style={{
                    ...songwriterStyles[block.type].inputStyle,
                    minHeight: "2.5rem",
                  }}
                >
                  {renderBlockText(block)}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Floating mobile-sidebar toggle button */}
  <button
        onClick={() => setIsSidebarOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-b from-blumine-500 to-blumine-600 text-white shadow-[0_8px_30px_rgba(32,146,186,0.35)] transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-[0_10px_35px_rgba(32,146,186,0.45)] xl:hidden border border-white/10"
      >
        <Video className="h-5.5 w-5.5" strokeWidth={2} />
        {shots.length > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-md border-2 border-white animate-pulse">
            {shots.length}
          </span>
        )}
      </button>

      {/* Mobile Drawer Background Blur Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/20 backdrop-blur-xs transition-all duration-300 xl:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Dedicated Fixed/Absolute Sidebar Panel (Identical layout structure to screenplay-side view) */}
      <div
        className={`fixed xl:sticky top-0 right-0 z-50 h-screen min-h-screen w-80 shrink-0 transform transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] xl:block ${
          isSidebarOpen
            ? "translate-x-0 shadow-[-10px_0_40px_rgba(16,43,60,0.08)]"
            : "translate-x-full"
        } xl:translate-x-0`}
      >
        <div className="sticky top-0 flex h-[100dvh] max-h-screen w-full flex-col border-l border-slate-100/50 bg-white/95 backdrop-blur-xl p-4 py-6">
          <div className="relative flex flex-col gap-3 py-4 flex-1 overflow-hidden">
            {/* Sidebar Header Section */}
            <div className="relative flex items-center justify-between mb-4 shrink-0 px-1">
              <div className="flex items-center gap-3">
                <div className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-blumine-100 bg-gradient-to-b from-white to-blumine-50/50 shadow-inner">
                  <Video
                    className="h-5 w-5 text-blumine-600"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="flex flex-col text-left">
                  <h3 className="text-[14px] font-bold uppercase tracking-wider text-blumine-800 leading-none">
                    Shots
                  </h3>
                  <span className="text-[9px] font-bold text-blumine-500 uppercase tracking-wide opacity-80 mt-1">
                    Lyrical Breakdown
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {cameras.length === 0 && (
                  <button
                    onClick={() => setIsInitModalOpen(true)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-blumine-200/60 bg-gradient-to-b from-white to-blumine-50/50 text-blumine-600 shadow-sm transition duration-150 active:scale-95 hover:bg-blumine-100/60 hover:text-blumine-700 shrink-0"
                    title="Configure Cameras"
                  >
                    <Settings2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                )}

                {(cameras.length > 0 || shots.length > 0) && (
                  <span className="flex items-center gap-1.5 rounded-full border border-blumine-200/50 bg-blumine-50/80 px-2.5 py-1 text-[10px] font-bold text-blumine-700 shadow-sm whitespace-nowrap">
                    {cameras.length > 0 && (
                      <span>
                        {cameras.length} Cam{cameras.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {cameras.length > 0 && shots.length > 0 && (
                      <span className="text-blumine-300/80 font-black">•</span>
                    )}
                    {shots.length > 0 && (
                      <span>
                        {shots.length} Shot{shots.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                )}

                {/* Mobile sidebar close toggle icon */}
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="flex xl:hidden h-8 w-8 items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition duration-150 active:scale-95"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Timelines Cards */}
            {shots.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-b from-white to-blumine-50/30 border border-blumine-100/60 shadow-inner mb-4">
                  <Video
                    className="h-5 w-5 text-blumine-400"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-xs font-bold text-blumine-800 tracking-wide">
                  No Shots Created Yet
                </p>
                <p className="text-[11px] text-blumine-400 mt-2 max-w-[180px] leading-relaxed font-medium">
                  Highlight text in the lyrics sheet to add your first camera
                  shot.
                </p>
              </div>
            ) : (
              <div className="flex h-full max-h-[calc(100vh-200px)] flex-col gap-3 overflow-y-auto overflow-x-hidden pb-4 pr-1 custom-scrollbar">
                {shots.map((shot) => {
                  const isActive = toggledShotId === shot.id;
                  return (
                    <div
                      key={shot.id}
                      onClick={() =>
                        setToggledShotId(isActive ? null : (shot.id ?? null))
                      }
                      className={`group relative flex-shrink-0 cursor-pointer rounded-2xl border transition-all duration-300 overflow-hidden text-left ${
                        isActive
                          ? "border-blumine-500 bg-blumine-50/30 shadow-[0_6px_20px_rgba(16,43,60,0.06)]"
                          : "border-blumine-100/50 bg-white hover:border-blumine-300 hover:shadow-[0_4px_14px_rgba(16,43,60,0.03)]"
                      }`}
                    >
                      <div
                        className={`absolute left-0 top-0 h-full w-[3px] transition-all duration-300 ${
                          isActive
                            ? "bg-blumine-500"
                            : "bg-transparent group-hover:bg-blumine-300"
                        }`}
                      />

                      <div className="flex flex-col gap-2.5 px-4 py-3.5">
                        <div className="flex items-start justify-between">
                          <div className="flex flex-col">
                            <span
                              className={`text-[10px] font-bold tracking-wider uppercase ${
                                isActive
                                  ? "text-blumine-500"
                                  : "text-blumine-400"
                              }`}
                            >
                              Shot {shot.shot_number}
                            </span>

                            <span className="text-xs font-extrabold text-blumine-800 leading-tight mt-1">
                              {shot.camera_name || "Unknown Camera"}
                            </span>
                          </div>

                          <span
                            className={`rounded-lg px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider transition-colors duration-200 ${
                              isActive
                                ? "bg-blumine-600 text-white shadow-sm"
                                : "bg-blumine-50/60 text-blumine-600 group-hover:bg-blumine-100"
                            }`}
                          >
                            {shot.shot_type}
                          </span>
                        </div>

                        {shot.parts && shot.parts.length > 0 && (
                          <div className="text-[10px] font-mono text-zinc-600 border-l border-blumine-100/50 pl-2 italic line-clamp-1">
                            "{shot.parts.map((p) => p.text).join(" ")}"
                          </div>
                        )}

                        <div className="flex items-center justify-between text-[11px] font-medium text-blumine-400">
                          <span>
                            {shot.lens_mm ? `${shot.lens_mm} Lens` : "—"}
                          </span>
                          <span className="text-[10px] font-bold text-blumine-300 opacity-0 group-hover:opacity-100 transition-opacity duration-200 tracking-wide">
                            Tap to expand
                          </span>
                        </div>

                        {/* Interactive edit expander */}
                        <div
                          className={`grid transition-all duration-300 ease-in-out ${
                            isActive
                              ? "grid-rows-[1fr] opacity-100 mt-1"
                              : "grid-rows-[0fr] opacity-0"
                          }`}
                        >
                          <div className="overflow-hidden">
                            <div className="pt-3 pb-1 border-t border-blumine-100/50 flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingShotId(shot.id ?? null);
                                }}
                                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-blumine-200/60 bg-white py-2 text-[11px] font-bold text-blumine-700 transition-all hover:bg-blumine-50 hover:text-blumine-900 shadow-sm active:scale-[0.97]"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUpdatingShotId(shot.id ?? null);
                                }}
                                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-b from-blumine-500 to-blumine-600 py-2 text-[11px] font-bold text-white transition-all hover:from-blumine-600 hover:to-blumine-700 shadow-md shadow-blumine-500/10 active:scale-[0.97]"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add New Shot Form Modal Layout */}
      {selectionMenu && (
        <ModalLayout
          onClose={handleCloseModal}
          title={
            cameras.length === 0 ? "Production Setup" : "Add New Lyrical Shot"
          }
          menuRef={menuRef}
          maxWidth={cameras.length === 0 ? "max-w-lg" : "max-w-7xl"}
        >
          {cameras.length === 0 ? (
            <LyricalProductionSetupModal
              initializeProduction={(count, type) => {
                initializeProduction(count, type);
              }}
            />
          ) : (
            <AddLyricalShotForm
              onSubmit={(values) => {
                addShot(values);
              }}
              cameras={cameras}
              shots={shots}
              movieCharacters={movieCharacters}
              handleCloseModal={handleCloseModal}
              parts={selectionMenu.parts}
            />
          )}
        </ModalLayout>
      )}

      {/* View Selected Shot Camera and Visual Assets Details Modal */}
      {viewingShotId && activeViewingShot && (
        <ModalLayout
          onClose={() => setViewingShotId(null)}
          title={`Shot ${activeViewingShot.shot_number.toString()} details`}
          maxWidth="max-w-7xl"
        >
          <ViewLyricalShotModal shot={activeViewingShot} />
        </ModalLayout>
      )}

      {/* Edit Existing Shot Form Modal Overlay */}
      {updatingShotId && (
        <ModalLayout
          onClose={() => setUpdatingShotId(null)}
          title={`Update Lyrical Shot ${
            shots
              .find((s) => s.id === updatingShotId)
              ?.shot_number?.toString()
              .padStart(2, "0") || "01"
          }`}
          maxWidth="max-w-7xl"
        >
          {shots.find((s) => s.id === updatingShotId) && (
            <AddLyricalShotForm
              parts={shots.find((s) => s.id === updatingShotId)!.parts}
              cameras={cameras}
              shots={shots}
              movieCharacters={movieCharacters}
              handleCloseModal={() => setUpdatingShotId(null)}
              userShotValue={shots.find((s) => s.id === updatingShotId)}
              onSubmit={(values) => {
                updateShot(updatingShotId, values);
                setUpdatingShotId(null);
              }}
            />
          )}
        </ModalLayout>
      )}

      {/* Manual camera configuration modal layout */}
      {isInitModalOpen && (
        <ModalLayout
          onClose={() => setIsInitModalOpen(false)}
          title="Camera Configuration Setup"
          maxWidth="max-w-lg"
        >
          <LyricalProductionSetupModal
            initializeProduction={async (count, type) => {
              await initializeProduction(count, type);
              setIsInitModalOpen(false);
            }}
          />
        </ModalLayout>
      )}
      </div>
    </div>
  );
}
